import { useContext, useEffect, useState } from 'react';
import {
  ColumnData,
  OrderListProps,
  OrderStatus,
  OrderStatusCode,
  OrderTypeProps,
  TableData,
} from '../interface/interface';
import { Table } from './Table';
import { Select } from './Select';
import moment from 'moment';
import CustomModal from './CustomModal';
import { AlertIcon, NotesIconBlue } from '../assets/svgImages';
import { Tooltip } from 'react-tooltip';
import { RowSelectionState } from '@tanstack/react-table';
import ReportsDatePicker from './OrderDatePicker';
import instance from '../config/axios';
import { useAuth } from 'react-oidc-context';
import showToast from './ToastComponent';
import { Link } from 'react-router-dom';
import { getOrderDetails } from '../service/getOrderSummary';
import { UserContext } from '../context/userContext';
import MedicationLabelCheckModal from './MedicationLabelCheckModal';
import Loading from './Loading';
import { AlertBoxModal } from './DispensingAlertModal';

export function OrderList(props: OrderListProps) {
  const [data, setTableData] = useState<Array<TableData>>([]);
  const [isOpen, setModalVisibility] = useState<boolean>(false);
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const [openConfirmationModal, setOpenConfirmationModal] =
    useState<boolean>(false);
  const [dispensingOrderId, setDispensingOrderId] = useState<number>();
  const [isDispendingConfirmationModalVisible, setDispensingModalVisibility] =
    useState<boolean>(false);
  const [totalQtyAlert, setTotalQtyAlert] = useState<boolean>(false);
  const [totalQty, setTotalQty] = useState<string>('');
  const [clickedOrder, setClickedOrder] = useState<TableData>();

  const { user } = useAuth();
  const userContext = useContext(UserContext);

  const [order, setOrderId] = useState<{
    orderId: number;
    invoiceId: string;
    orderStatusId: number;
  }>();

  const resetSelection = () => {
    setRowSelection({});
  };

  const columns: Array<ColumnData> = [
    {
      key: 'select',
      header: ({ table }) => {
        const allRowsSelected =
          table.getRowModel().rows?.length > 0 &&
          table.getRowModel().rows.every((row) => row.getIsSelected());
        const handleSelectAll = () => {
          const shouldSelectAll = !allRowsSelected;
          table.getRowModel().rows.forEach((row) => {
            row.getToggleSelectedHandler()(shouldSelectAll);
          });
        };

        return (
          <input
            type="checkbox"
            checked={allRowsSelected}
            onChange={handleSelectAll}
          />
        );
      },
      cell: ({ row, getValue }) => (
        <input
          type="checkbox"
          checked={row.getIsSelected() || rowSelection?.[getValue()]}
          disabled={!row.getCanSelect()}
          onChange={row.getToggleSelectedHandler()}
        />
      ),
    },
    {
      key: 'orderId',
      header: () => 'Order ID',
      cell: (info) => {
        const noteID = info.row.original.orderId;
        return (
          <p className="flex">
            {info.row.original.substituteCount > 0 && (
              <>
                <a data-tooltip-id={noteID}>
                  <AlertIcon className="absolute left-1 fill-secondary-pink-500 cursor-pointer" />
                </a>
                <Tooltip className="z-[200]" id={noteID}>
                  <p>Contains Substitute Products</p>
                </Tooltip>
              </>
            )}
            {info.row.original.allowedProductCount > 0 && (
              <>
                <a data-tooltip-id={noteID}>
                  <AlertIcon className="absolute left-1 fill-secondary-pink-500 cursor-pointer" />
                </a>
                <Tooltip className="z-[200]" id={noteID}>
                  <p>Contains Allowed Products</p>
                </Tooltip>
              </>
            )}
            <span className="ml-2">{info.getValue()}</span>
          </p>
        );
      },
      isSortable: true,
    },
    {
      key: 'createdDate',
      header: () => 'Ordered Date',
      cell: (info) => {
        const date = moment(info.getValue());
        return date.isValid() ? date.format('DD/MM/YYYY HH:mm') : '-';
      },
      isSortable: true,
    },

    {
      key: 'patientName',
      header: () => 'Patient Name',
      cell: (info) => {
        return (
          <>
            {info.getValue()}
            {info.row.original.isPatientFirstOrder &&
              !info.row.original?.isGuest && (
                <span className="ml-2 text-xs bg-primary-blue-500 text-white px-2 py-1 rounded">
                  First Order
                </span>
              )}
            {info.row.original?.isGuest ? (
              <span className="ml-2 px-3 py-1 rounded-full uppercase text-xs text-white font-semibold bg-secondary-green-500">
                Guest
              </span>
            ) : (
              <></>
            )}
          </>
        );
      },
      isSortable: true,
    },
    {
      key: 'dateOfBirth',
      header: () => 'Date of Birth',
      cell: (info) => {
        const date = moment(new Date(info.getValue()));
        return date.isValid() ? moment.utc(date).format('DD/MM/YYYY') : '-';
      },
    },
    {
      key: 'itemCount',
      header: () => 'Items',
      cell: (info) => info.getValue(),
    },
    {
      key: 'currentStatus',
      header: () => 'Current Status',
      cell: (info) => {
        return (
          <>
            <span
              className="w-2 h-2 mr-2 rounded-full inline-block"
              style={{ backgroundColor: info.getValue().colorCode }}
            ></span>
            <span>{info.getValue().name}</span>
          </>
        );
      },
    },
    {
      key: 'updateStatus',
      header: () => 'Change status',
      cell: (cell) => {
        const val = cell.getValue();
        const expectedStatus = props.select.data;
        const valToIncrement =
          val.currentSelectedId === OrderStatusCode.DISPENSED ? 2 : 1;

        const statusToRender = expectedStatus?.find(
          (f) => f.id === val.currentSelectedId + valToIncrement,
        );

        return (
          <>
            {!props.select.loading &&
              ![
                OrderStatusCode.INVALID,
                OrderStatusCode.CANCELLED,
                OrderStatusCode.SHIPPED,
                OrderStatusCode.CANCELLATION_REQUEST,
                OrderStatusCode.PAUSED,
                OrderStatusCode.AWAITING_PAYMENT,
                OrderStatusCode.AUSPOST_FAILURE,
                OrderStatusCode.AWAITING_SAS_B,
              ].includes(val.currentSelectedId) &&
              !cell.row.original.isShipmentFailed && (
                <button
                  type="button"
                  style={
                    val.currentSelectedId === OrderStatusCode.AMENDING_DISPENSE
                      ? {
                          backgroundColor: expectedStatus.find(
                            (ex) => ex.id === OrderStatusCode.READY_FOR_SCAN,
                          )?.color_code,
                        }
                      : {
                          backgroundColor: expectedStatus.find(
                            (ex) => ex.id === val.currentSelectedId + 1,
                          )?.color_code,
                        }
                  }
                  className={(() => {
                    const commonClasses =
                      'font-semibold py-2 rounded text-xs w-36';
                    if (val.name === OrderStatus.SHIPPED) {
                      return (
                        'bg-secondary-green-500 text-white cursor-not-allowed ' +
                        commonClasses
                      );
                    } else if (val.name === OrderStatus.CANCEL) {
                      return (
                        'bg-primary-red-500 text-white cursor-not-allowed ' +
                        commonClasses
                      );
                    } else {
                      return 'text-primary-blue-500 ' + commonClasses; // Default color
                    }
                  })()}
                  onClick={() => {
                    // If Update Status was shipped we confirm with the user
                    if (
                      val.currentSelectedId + 1 ===
                      OrderStatusCode.DISPENSED
                    ) {
                      setOrderId({
                        orderId: val.orderId,
                        invoiceId: val.invoiceId,
                        orderStatusId: statusToRender?.id as number,
                      });
                      setClickedOrder(cell.row.original);
                      setModalVisibility(true);
                    } else if (
                      val.currentSelectedId ===
                      OrderStatusCode.AMENDING_DISPENSE
                    ) {
                      props.updateOrderStatus({
                        orderId: val.orderId,
                        orderStatusIdToUpdate: OrderStatusCode.READY_FOR_SCAN,
                      });
                    } else if (
                      val.currentSelectedId + 2 ===
                      OrderStatusCode.READY_FOR_SHIPPING
                    ) {
                      setOrderId({
                        orderId: val.orderId,
                        invoiceId: val.invoiceId,
                        orderStatusId: statusToRender?.id as number,
                      });
                      setClickedOrder(cell.row.original);
                      setModalVisibility(true);
                    } else if (
                      val.currentSelectedId + 1 ===
                      OrderStatusCode.READY_FOR_SCAN
                    ) {
                      props.updateOrderStatus({
                        orderId: val.orderId,
                        orderStatusIdToUpdate:
                          OrderStatusCode.READY_FOR_SHIPPING,
                      });
                    } else {
                      props.updateOrderStatus({
                        orderId: val.orderId,
                        orderStatusIdToUpdate: val.currentSelectedId + 1,
                      });
                    }
                  }}
                >
                  {val.currentSelectedId === OrderStatusCode.AMENDING_DISPENSE
                    ? 'Ready for scan'
                    : statusToRender?.name}
                </button>
              )}
          </>
        );
      },
    },
    {
      key: 'assignedTo',
      header: () => 'Assigned to',
      cell: (cell) => {
        const val = cell.getValue();
        const dontShowNameForStatus = [OrderStatusCode.ORDERED];
        if (dontShowNameForStatus.includes(val.currentOrderStatusId)) return '';
        return val.name;
      },
    },
    {
      key: 'updatedDate',
      header: () => 'Last Edited',
      cell: (info) => {
        const date = moment(info.getValue());
        return date.isValid() ? date.format('DD/MM/YYYY HH:mm') : '-';
      },
      isSortable: true,
    },
    {
      key: 'moreDetails',
      header: () => '',
      cell: (cell) => {
        const orderID = `order-note-${cell.row.original.orderId}`;
        return (
          <div className="flex items-center justify-center">
            {cell.row.original.note && (
              <>
                <a data-tooltip-id={orderID}>
                  <NotesIconBlue />
                </a>

                <Tooltip className="z-[200]" id={orderID}>
                  <p>{cell.row.original.note}</p>
                </Tooltip>
              </>
            )}
            <Link
              className="text-red-900 underline flex justify-start ml-2"
              onClick={() => checkStatus(cell.row.original)}
              to=""
            >
              View Order
            </Link>
          </div>
        );
      },
    },
  ];

  const checkStatus = async (selectedOrder: TableData) => {
    // Fetch the latest status from API in case websocket not updated
    const orderDetails = await getOrderDetails(
      user?.access_token ?? '',
      selectedOrder?.moreDetails?.id,
    );

    if (
      orderDetails?.currentStatus?.id === OrderStatusCode.DISPENSING &&
      orderDetails?.assignedUser !== userContext.userDetail?.id
    ) {
      setDispensingOrderId(selectedOrder?.moreDetails?.id);
      setDispensingModalVisibility(true);
    } else {
      window.location.href = `/order-summary/${selectedOrder?.moreDetails?.id}`;
    }
  };

  useEffect(() => {
    userContext.setLoadingEnable(false);
    setTableData(
      props?.orderItem?.map((order) => ({
        select: order.id,
        createdDate: order.createdDate,
        assignedTo: {
          name: order.assignedTo as string,
          currentOrderStatusId: order.orderStatus.id,
        },
        patientName: order.patientName,
        dateOfBirth: order.patientDob,
        itemCount: order.orderProducts
          ?.map((ele) => ele?.order_quantity)
          ?.reduce((a: number, b: number) => a + b, 0),
        substituteCount: order?.substituteCount,
        allowedProductCount: order?.allowedProductCount,
        currentStatus: {
          id: order.orderStatus.id,
          name: order.orderStatus.name,
          colorCode: order.orderStatus.colorCode,
        },
        updateStatus: {
          currentSelectedId: order.orderStatus.id,
          name: order.orderStatus.name,
          orderId: order.id,
          invoiceId: order.invoiceId,
        },
        moreDetails: { id: order.id },
        orderId: order.invoiceId,
        note: order.note,
        updatedDate: order.updatedDate,
        isShipmentFailed: order.isShipmentFailed,
        isPatientFirstOrder: order.isPatientFirstOrder,
        isGuest: order?.isGuest,
      })),
    );
  }, [props.orderItem]);

  const changeStatus = async () => {
    const keys = Object.keys(rowSelection ?? {});
    const selectedOrders: number[] = [];

    for (const k of keys) {
      if (rowSelection?.[k]) selectedOrders.push(Number(k));
    }

    if (!selectedOrders?.length) {
      showToast({
        type: 'warning',
        message: 'Select atleast one order to update status',
      });
      return;
    }

    if (!props.select.selected.length) {
      showToast({
        type: 'warning',
        message: 'Order status cannot be empty',
      });
      return;
    }

    if (props.select.selected.length > 1) {
      showToast({
        type: 'warning',
        message: 'Please select one status to update the orders',
      });
      return;
    }

    if (
      [
        OrderStatusCode.INVALID,
        OrderStatusCode.SHIPPED,
        OrderStatusCode.CANCELLED,
        OrderStatusCode.CANCELLATION_REQUEST,
        OrderStatusCode.PAUSED,
      ].includes(props.select.selected[0].id)
    ) {
      showToast({
        type: 'warning',
        message: 'Please select valid order status',
      });
      return;
    }

    setOpenConfirmationModal(true);
  };

  const bulkUpdate = async () => {
    setOpenConfirmationModal(false);
    userContext.setLoadingEnable(true);
    const keys = Object.keys(rowSelection ?? {});
    const selectedOrders: number[] = [];

    for (const k of keys) {
      if (rowSelection?.[k]) selectedOrders.push(Number(k));
    }

    await instance({
      method: 'PUT',
      url: 'api/v1/order/bulk/update',
      data: {
        orderIds: selectedOrders,
        statusId: props.select.selected[0].id,
      },
      headers: {
        Authorization: `Bearer ${user?.access_token}`,
      },
    }).then((res) => {
      if (res.status === 200) {
        showToast({
          message: 'All records have been updated',
          type: 'success',
        });
      } else {
        showToast({
          message: res.data.message,
          type: 'warning',
        });
      }
      resetSelection();
      userContext.setLoadingEnable(false);
      props.setFilterApplyStatus((prev) => !prev);
    });
  };

  const renderDispensingModal = () => {
    return (
      <AlertBoxModal
        isOpen={isDispendingConfirmationModalVisible}
        message="This order is already being dispensed."
        onOk={() => {
          window.location.href = `/order-summary/${dispensingOrderId}`;
          setDispensingModalVisibility(false);
        }}
        onCancel={() => {
          setDispensingModalVisibility(false);
        }}
      />
    );
  };

  if (userContext.loadingEnable) return <Loading />;

  return (
    <>
      <div>
        <div>
          <div className="flow-root">
            <div className="-mx-4 -my-2 sm:-mx-6 lg:-mx-8 min-h-[600px]">
              <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8 relative">
                <div className="flex items-center  mb-5 relative bg-white border shadow p-2.5 rounded">
                  <span className="text-grey-500 text-xs mr-3">
                    Filter orders
                  </span>
                  <div className="min-w-40 2.5xl:min-w-48 status-filter">
                    <Select
                      placeholder="Status"
                      options={props.select.data?.filter(
                        (orderStatus) =>
                          orderStatus.id !== OrderStatusCode.AMENDING_DISPENSE,
                      )}
                      selectBoxLabel={'Filter By Order Status'}
                      defaultSelected={props.select.selected}
                      onChange={(el) => {
                        {
                          props.select.selectHandler(
                            el.map((e) => ({
                              id: e.value as number,
                              name: e.label,
                              color_code: '',
                            })),
                          );
                          resetSelection();
                          props.search.submitSearch((p) => p + 1);
                        }
                      }}
                      loading={props.select.loading}
                    />
                  </div>

                  <div>
                    <ReportsDatePicker
                      onExport={(from, to, filterBy) => {
                        props.setFilterDate({ to, from, filterBy });
                        props.setFilterApplyStatus((prev) => !prev);
                        props.search.submitSearch((p) => p + 1);
                      }}
                      onClear={() => {
                        props.setFilterDate(undefined);
                        props.setFilterApplyStatus((prev) => !prev);
                        props.select.selectHandler([]);
                        props.search.submitSearch((p) => p + 1);
                      }}
                      btnText="Filter"
                      order={true}
                      setSortOrder={props.setSortOrder}
                      sort={props?.sort}
                      keyStores={{
                        from: '',
                        to: '',
                      }}
                      filterBy={props.filterBy}
                      setFirstOrderFilter={props.setFirstOrderFilter}
                      setguestOrderFilter={props.setGuestOrderFilter}
                    />
                  </div>
                </div>
                <Table
                  columns={columns}
                  data={
                    props.firstOrderFilter
                      ? data.filter((x) => x.isPatientFirstOrder && !x?.isGuest)
                      : props.guestOrderFilter
                        ? data.filter((x) => x?.isGuest)
                        : data
                  }
                  enableSorting={true}
                  enablePagination={true}
                  enableSearch={true}
                  enableRowSelection
                  search={props.search}
                  rowSelection={{
                    state: rowSelection ?? {},
                    setter: setRowSelection,
                  }}
                  pagintation={{
                    state: props.pagination.state,
                    setter: props.pagination.setter,
                    totalRecords: props.pagination.totalRecords,
                  }}
                  order={OrderTypeProps.ORDER}
                  orderBulkUpdate={{
                    state: props.select.selected,
                    action: changeStatus,
                  }}
                  childLoader={props.childLoader}
                  firstOrderFilter={props.firstOrderFilter}
                  setFirstOrderFilter={props.setFirstOrderFilter}
                  guestOrderFilter={props.guestOrderFilter}
                  setGuestOrderFilter={props.setGuestOrderFilter}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      <CustomModal
        isOpen={isOpen}
        onClose={() => {
          setModalVisibility(false);
          setTotalQtyAlert(false);
          setTotalQty('');
        }}
        header="Confirmation"
        footer={
          <>
            <button
              className={`mr-4 bg-primary-blue-500 text-white px-4 py-2 w-24 rounded  ${totalQtyAlert || !totalQty ? 'cursor-not-allowed opacity-50' : ''}`}
              onClick={() => {
                props.updateOrderStatus({
                  orderId: order?.orderId as number,
                  orderStatusIdToUpdate: order?.orderStatusId as number,
                });
                setModalVisibility(false);
                setTotalQtyAlert(false);
                setTotalQty('');
              }}
              disabled={totalQtyAlert || !totalQty}
            >
              Yes
            </button>
            <button
              className="bg-white border border-solid border-primary-blue-500 text-primary-blue-500 w-24 px-4 py-2 rounded"
              onClick={() => {
                setModalVisibility(false);
                setTotalQtyAlert(false);
                setTotalQty('');
              }}
            >
              Cancel
            </button>
          </>
        }
      >
        {`Do you really want to ${clickedOrder?.currentStatus.id === OrderStatusCode.DISPENSING ? 'dispense' : 'move the order to Ready for Shipping'}`}{' '}
        <b className="text-primary-blue-500">#{order?.invoiceId}</b>
        <MedicationLabelCheckModal
          currentStatusId={clickedOrder?.currentStatus.id as number}
          isQtyAlert={totalQtyAlert}
          totalQty={totalQty}
          itemCount={clickedOrder?.itemCount as number}
          setTotalQty={setTotalQty}
          setTotalQtyAlert={setTotalQtyAlert}
        />
      </CustomModal>
      <CustomModal
        isOpen={openConfirmationModal}
        onClose={() => setOpenConfirmationModal(false)}
        header="Status Update Confirmation"
        modalSize={'w-1/3'}
        footer={
          <>
            <button
              className="bg-white border border-solid border-primary-blue-500 text-primary-blue-500  px-4 py-2 rounded "
              onClick={() => setOpenConfirmationModal(false)}
            >
              Cancel
            </button>
            <button
              className="ml-4 bg-secondary-green-700 text-white px-4 py-2  rounded"
              onClick={() => {
                bulkUpdate();
              }}
            >
              Confirm Update
            </button>
          </>
        }
      >
        <p className="text-base">
          You are about to update the status for all the selected records.
          Proceed with the following status change?
        </p>
        <p className="mt-3 text-base text-primary-blue-500 font-semibold">
          New Status:
          <span className="font-regular ml-3 text-black">
            {props?.select?.selected[0]?.id ===
            OrderStatusCode.AMENDING_DISPENSE
              ? 'Read for scan'
              : props?.select?.data?.find(
                  (x) => x?.id === props?.select?.selected[0]?.id + 1,
                )?.name}
          </span>
        </p>
      </CustomModal>
      {renderDispensingModal()}
    </>
  );
}
